/**
 * @file Minimal typed API client for the Worker (T0.7).
 *
 * Deliberately tiny: `fetch`, and the Worker's error envelope turned into a thrown
 * `ApiError`. TanStack Query owns caching/retries (see ./queries.ts), so this layer
 * stays a pure transport with no state — which is also what makes it unit-testable
 * by injecting `fetchImpl`.
 *
 * AUTHENTICATION IS A COOKIE THE BROWSER OWNS, NOT SOMETHING THIS MODULE HANDLES.
 * The session is Better Auth's first-party, HttpOnly cookie. This client never sees
 * it, never stores it and never sends an `Authorization` header: it calls the API
 * SAME-ORIGIN under `/api` (the Vite proxy locally, a Worker route on the SPA's
 * hostname in staging), so the browser attaches the cookie itself. That is also why
 * there is no `credentials: "include"` — same-origin requests carry it by default,
 * and a bearer token would have had to live in `localStorage`, which this app bans
 * (PLAN.md §8). A signed-out request simply comes back 401 from the server.
 */
import type {
  ApiExpenseEntry,
  ApiGuarantorContact,
  ApiLease,
  ApiLedgerEntry,
  ApiMaintenanceEvent,
  ApiMaintenanceRequest,
  ApiProperty,
  ApiPropertyWithUnits,
  ApiTenantDetail,
  ApiUnit,
  ApiUnitDetail,
  ApiWorkspace,
  BalancesResponse,
  ErrorEnvelope,
  ExpenseListResponse,
  GuarantorListResponse,
  LedgerListResponse,
  LeaseDetailResponse,
  LeaseLedgerResponse,
  LeaseListResponse,
  LeaseRentChargesResponse,
  MaintenanceListResponse,
  MaintenanceRequestDetailResponse,
  MeResponse,
  OverviewResponse,
  PaymentListResponse,
  PropertyListResponse,
  PropertyPnlResponse,
  RentChargeGenerationResponse,
  RentChargeListResponse,
  ScheduleEReportResponse,
  TeamListResponse,
  TenantListResponse,
  TurnoverResponse,
  UnitListResponse,
  WorkspaceSettingsResponse,
  InvitationAcceptedResponse,
  InvitationCreatedResponse,
  InvitationListResponse,
} from "./types";
import type {
  ActivityPageDto,
  DocumentDomain,
  DocumentUploadDto,
  ExpenseEntryCreateBody,
  GuarantorConsentBody,
  GuarantorContactCreateBody,
  LeaseCreateBody,
  LeaseStatus,
  LeaseTenantUpdateBody,
  LeaseUpdateBody,
  LedgerCorrectionCreateBody,
  MaintenanceEventCreateBody,
  MaintenanceRequestCreateBody,
  MaintenanceStatus,
  PaymentCreateBody,
  PaymentRecordedDto,
  PropertyCreateBody,
  UnitCreateBody,
  WorkspaceSettingsUpdateBody,
  AssignableTeamRole,
  MemberRoleChangedDto,
  WorkspaceRenamedDto,
} from "@neighboros/shared";

/**
 * One validation problem, as the API reports it.
 *
 * `path` is the field the problem belongs to (`["purchaseDate"]`, `["units", 1,
 * "label"]`). Surfacing it is the difference between "invalid property" and
 * "purchase date: must be a YYYY-MM-DD date" — and the API has always sent it; the
 * client was discarding it, so a rejected save gave a person nothing to act on.
 */
export interface ApiFieldIssue {
  readonly path: readonly (string | number)[];
  readonly message: string;
}

/** Thrown for every non-2xx response and for a missing session token. */
export class ApiError extends Error {
  /** HTTP status, or 401 when no token was available to send. */
  readonly status: number;
  /** The Worker's machine-readable `error.code` (e.g. `unauthorized`). */
  readonly code: string;
  /** Per-field validation problems, when the route returned any. */
  readonly issues: readonly ApiFieldIssue[];

  constructor(
    status: number,
    code: string,
    message: string,
    issues: readonly ApiFieldIssue[] = [],
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.issues = issues;
  }
}

/**
 * The filters the Reports page offers (T2.11).
 *
 * Every field is optional and an EMPTY STRING means "no filter" — the state a
 * blank control produces. There is deliberately no default range: a default period
 * would imply a fiscal year the platform was never given, and inventing one is
 * founder-owned policy.
 */
/** The three bulk CSV exports. */
export type ExportKind = "ledger" | "payments" | "tenants";

export interface ScheduleEReportParams {
  /** Inclusive lower bound, `YYYY-MM-DD`. */
  readonly from?: string;
  /** Inclusive upper bound, `YYYY-MM-DD`. */
  readonly to?: string;
  readonly propertyId?: string;
}

/** Which page of a keyset-paged list to read: the previous page's `nextCursor`, and an optional size. */
export interface PageParams {
  readonly cursor?: string | undefined;
  readonly limit?: number;
}

/** `?limit=&cursor=` for a paged read, or nothing for the first default page. */
function pageQuery(page: PageParams | undefined): string {
  const params = new URLSearchParams();
  if (page?.limit !== undefined) params.set("limit", String(page.limit));
  if (page?.cursor !== undefined) params.set("cursor", page.cursor);
  const text = params.toString();
  return text === "" ? "" : `?${text}`;
}

/** The surface the SPA uses. Grows one route at a time, with the API. */
export interface ApiClient {
  /** GET /me — the signed-in user plus every workspace they belong to. */
  me(): Promise<MeResponse>;
  /** POST /workspaces — create a workspace; the caller becomes its owner. */
  createWorkspace(name: string): Promise<ApiWorkspace>;
  /** GET /workspaces/:id/overview — the dashboard's aggregate numbers. */
  overview(workspaceId: string): Promise<OverviewResponse>;
  /** GET /workspaces/:id/properties */
  listProperties(workspaceId: string): Promise<PropertyListResponse>;
  /** GET /workspaces/:id/units — every unit in the portfolio, with its property. */
  listUnits(workspaceId: string): Promise<UnitListResponse>;
  /**
   * GET /workspaces/:id/units/:unitId — one unit, its property, its leases and its
   * maintenance, in ONE request.
   *
   * A unit's own page needs all four, and three sequential requests would flash an
   * incomplete panel on every visit — the same reasoning `getProperty` returns its
   * units inline. `leases` is a list because a unit can carry a successor during
   * turnover, and `currentLeaseId` (computed server-side by the shared
   * `currentLiveLeaseId`) names the one in force so the page does not invent its own
   * reading of the §6.2 states.
   */
  getUnit(workspaceId: string, unitId: string): Promise<ApiUnitDetail>;
  /** GET /workspaces/:id/leases — every lease, with its unit, property and tenants. */
  listLeases(workspaceId: string): Promise<LeaseListResponse>;
  /**
   * GET /workspaces/:id/leases/:leaseId — ONE lease and its people.
   *
   * The lease detail screen's spine. Its balance and its charges are two more
   * requests (`getLeaseLedger`, `getLeaseRentCharges`) rather than one aggregate
   * endpoint: an aggregate would be a fourth shape for three payloads that already
   * exist and are already proved, and the three share no query plan.
   */
  getLease(workspaceId: string, leaseId: string): Promise<LeaseDetailResponse>;
  /**
   * GET /workspaces/:id/leases/:leaseId/ledger — the lease's ledger, with its
   * balance and its per-person attribution.
   *
   * `balanceCents` is computed server-side by the one balance rule
   * (`netBalanceCents`), so the screen renders it rather than adding rows up itself.
   * On an EMPTY ledger it is 0, and the screen is what must say "nothing has been
   * posted" rather than dressing that arithmetic zero up as a fact about the world.
   */
  getLeaseLedger(workspaceId: string, leaseId: string): Promise<LeaseLedgerResponse>;
  /**
   * GET /workspaces/:id/leases/:leaseId/rent-charges — what this lease owes, period
   * by period.
   */
  getLeaseRentCharges(
    workspaceId: string,
    leaseId: string,
  ): Promise<LeaseRentChargesResponse>;
  /**
   * POST /workspaces/:id/leases — a lease AND its tenants in one request.
   *
   * One transaction, all or nothing, for the reason the property form takes its
   * units: a four-person house is one agreement, and four follow-up POSTs would
   * leave a half-written lease if connectivity dropped halfway.
   *
   * `status` is deliberately absent from `LeaseCreateBody`: the §6.2 machine owns
   * it, the column defaults to `draft`, and only `transitionLease` below may move
   * it — a client that could set it would skip the transition route and its audit
   * row.
   */
  createLease(workspaceId: string, body: LeaseCreateBody): Promise<ApiLease>;
  /**
   * POST /workspaces/:id/leases/:leaseId/transitions — §6.2's one write path.
   *
   * The `to` state must be one the API's own map allows from the lease's current
   * one. The UI derives its buttons from the SAME map (`nextLeaseStatuses`), so an
   * illegal move should be unreachable from the screen — but the route is the
   * authority and answers 409 when the map disagrees, or when another caller moved
   * the lease first. Both reach the person through `FormError`.
   */
  transitionLease(
    workspaceId: string,
    leaseId: string,
    to: LeaseStatus,
  ): Promise<ApiLease>;
  /**
   * PATCH /workspaces/:id/leases/:leaseId — edit a lease's own fields.
   *
   * The client's first caller is the lease PDF attachment (T0.8 Phase B): the signed
   * agreement is a FIELD of the lease, so it is written with the same PATCH the API
   * already offers rather than through a second route invented for a file. `unitId`
   * and `tenants` are not in `LeaseUpdateBody` — the schema is `partial()` on create
   * with those two omitted — so this cannot move a tenancy or replace the people on
   * it. `pdfFileId: null` detaches the attachment; an id attaches a document that the
   * route verifies is this workspace's and of the `lease` domain.
   *
   * The response is the same shape `getLease` returns, so the page can write it
   * straight into its cache without a second read.
   */
  updateLease(
    workspaceId: string,
    leaseId: string,
    body: LeaseUpdateBody,
  ): Promise<LeaseDetailResponse>;
  /** GET /workspaces/:id/tenants — every person on a lease, with where they live. */
  listTenants(workspaceId: string): Promise<TenantListResponse>;
  /**
   * GET /workspaces/:id/tenants/:tenantId — ONE person, with their lease, their unit
   * and property, their share, their occupancy dates and the money attributed to them.
   *
   * The tenant's own page's spine, and the reason a Tenants row no longer has to open
   * a lease to say anything about a person. It carries the whole breadcrumb chain
   * (`Properties › 123 N Main St › Apt 1 › Ada Reyes`), so the page renders complete on
   * the first paint rather than filling in after a second request.
   */
  getTenant(workspaceId: string, tenantId: string): Promise<ApiTenantDetail>;
  /**
   * PATCH /workspaces/:id/tenants/:tenantId — edit one person's contact and share
   * details.
   *
   * **`leaseId` and `userId` are not in `LeaseTenantUpdateBody` and cannot be sent
   * through this method**, so "moving a person between agreements" is not expressible
   * from the SPA at all: the shape has no such field, and the route refuses a body that
   * names one with `tenant_link_not_editable_here` rather than answering 200 to a
   * no-op. The response is the same payload `getTenant` returns, so the page re-reads
   * nothing and cannot drift from the detail view.
   */
  updateTenant(
    workspaceId: string,
    tenantId: string,
    body: LeaseTenantUpdateBody,
  ): Promise<ApiTenantDetail>;
  /**
   * GET /workspaces/:id/ledger — every ledger entry in the portfolio, newest
   * first, with its lease, unit, property and attributed person.
   *
   * There is deliberately no `createLedgerEntry` here. Money is entered through
   * `recordPayment` (T1.5) — the ledger's first writer, and the only one — because
   * a payment has to settle a named charge and move it in the same transaction; a
   * bare append is a route the API offers and no screen asks for. Fixing a row that
   * was entered wrongly is `correctLedgerEntry` below.
   */
  listLedger(workspaceId: string, page?: PageParams): Promise<LedgerListResponse>;
  /**
   * POST /workspaces/:id/ledger/:entryId/correction — replace an entry by APPENDING.
   *
   * The ledger's one door for a mistake, and the reason there is no PATCH: the
   * route inserts a NEW row carrying the corrected type, amount and attribution
   * (`correctionOf` naming the row it replaces) and marks the original `superseded`
   * in the same transaction. Both rows stay readable forever; the balance counts the
   * replacement once. The body is the full replacement — `amountCents` is SIGNED and
   * must agree with `entryType` (a payment stays negative) — and `memo` is REQUIRED:
   * a correction is the only thing that changes what a previous row means, and the
   * reason is the one fact the new row cannot derive from the old.
   *
   * 409 `ledger_entry_already_corrected` when the entry is no longer live (someone
   * corrected it first); the caller re-reads and corrects the replacement instead.
   */
  correctLedgerEntry(
    workspaceId: string,
    entryId: string,
    body: LedgerCorrectionCreateBody,
  ): Promise<ApiLedgerEntry>;
  /**
   * GET /workspaces/:id/rent-charges — every charge in the portfolio, newest
   * period first, with its lease, unit, property and the people on it.
   *
   * There is still deliberately no `transitionRentCharge` here: the §6.1 route
   * exists and is proved by the API suite, and no screen writes a charge's status,
   * so a client method with no caller would be the first half of a capability
   * nobody has built. `generateRentCharges` below now HAS a caller — the button on
   * this screen — which is why it is here.
   */
  listRentCharges(workspaceId: string, page?: PageParams): Promise<RentChargeListResponse>;
  /**
   * POST /workspaces/:id/rent-charges/generate — run §6.4 now.
   *
   * The same code path the scheduled job runs, so a landlord who has just
   * activated a lease does not wait for a tick that the local stack has no cron
   * to deliver. **Idempotent**: the insert is `ON CONFLICT … DO NOTHING` against
   * the per-period unique constraint (migration 0007), so pressing the button
   * twice is not a mistake and the second call reports `chargesCreated: 0`.
   *
   * The response is the counts themselves — `leasesScanned`, `chargesCreated`,
   * `chargesExisting` — so the screen can report a real number rather than
   * claiming "charges generated".
   */
  generateRentCharges(workspaceId: string): Promise<RentChargeGenerationResponse>;
  /**
   * GET /workspaces/:id/payments — every payment recorded in the portfolio, newest
   * first, with its charge, lease, unit, property and attributed person.
   */
  listPayments(workspaceId: string, page?: PageParams): Promise<PaymentListResponse>;
  /** GET /workspaces/:id/activity — the audit trail, newest first, keyset-paged. */
  listActivity(workspaceId: string, page?: PageParams): Promise<ActivityPageDto>;
  /**
   * GET /workspaces/:id/balances — what each unit owes, and what each person paid.
   *
   * A read only. It is the API's answer to PLAN.md §10 T1.5's "unit-owes +
   * per-tenant views", and the per-tenant half reports what a person PAID while
   * saying in the payload why it will not report what they OWE.
   */
  listBalances(workspaceId: string): Promise<BalancesResponse>;
  /**
   * GET /workspaces/:id/maintenance — every maintenance request in the portfolio,
   * newest first, with its unit, property, channel tag and tenancy.
   */
  listMaintenance(workspaceId: string): Promise<MaintenanceListResponse>;
  /**
   * POST /workspaces/:id/maintenance — §6.3's on-behalf logging: a landlord records
   * a request a tenant reported off-platform.
   *
   * `channel` is one of the three on-behalf instruments (`text`, `call`,
   * `in_person`) — `portal` is the tenant's instrument (T1.7) and the API refuses
   * it here, because a landlord-side caller cannot have seen a portal submission.
   */
  createMaintenanceRequest(
    workspaceId: string,
    body: MaintenanceRequestCreateBody,
  ): Promise<ApiMaintenanceRequest>;
  /**
   * GET /workspaces/:id/maintenance/:requestId — one request and its TIMELINE.
   *
   * The route existed (and was proved by the API suite) before any screen read it; the
   * request's own page is what makes it a caller rather than a route kept "for later".
   * The events are oldest first — a history reads forward, unlike the work list.
   */
  getMaintenanceRequest(
    workspaceId: string,
    requestId: string,
  ): Promise<MaintenanceRequestDetailResponse>;
  /**
   * POST /workspaces/:id/maintenance/:requestId/transitions — §6.3's one write path.
   *
   * The `to` state must be one the API's own map allows from the request's current one;
   * the page derives its buttons from the SAME `MAINTENANCE_TRANSITIONS` map, so an
   * illegal move should be unreachable from the screen. The route is the authority and
   * answers 409 when the map disagrees, when another caller moved the request first, or
   * when a reopen has missed the §6.3 14-day window.
   */
  transitionMaintenanceRequest(
    workspaceId: string,
    requestId: string,
    to: MaintenanceStatus,
  ): Promise<ApiMaintenanceRequest>;
  /**
   * POST /workspaces/:id/maintenance/:requestId/events — append a NOTE to the timeline.
   *
   * A note writes the timeline row only: it is not a state move, so it writes no
   * `status_transitions` row, and the `kind` is the route's to set. `photoFileIds` is
   * accepted: the ids come from `uploadDocument` with the `maintenance` domain, and
   * the route verifies each one names a document of THIS workspace for that purpose
   * before the note is written — a body that names anything else is a 400, not a
   * silently dropped field.
   */
  addMaintenanceNote(
    workspaceId: string,
    requestId: string,
    body: MaintenanceEventCreateBody,
  ): Promise<ApiMaintenanceEvent>;
  /**
   * POST /workspaces/:id/documents?domain=… — upload ONE file, as raw bytes.
   *
   * **This cannot go through `request<T>`**, and that is the whole reason it exists
   * separately. `request` sets `Content-Type: application/json` whenever there is a
   * body and `JSON.stringify`s it: a file is not JSON, and the upload route reads the
   * BYTES of the request body and takes the media type from the `Content-Type`
   * header. So this is the client's second raw-body path — the first being
   * `requestBlob` in the other direction — and it is deliberately explicit rather
   * than a flag on `request`: a flag would make every caller decide, and the wrong
   * choice fails at runtime on a body the server reads as a corrupted file.
   *
   * The PURPOSE travels as the `domain` query parameter, mirroring the route: one
   * body with one declared type is a contract a caller cannot get half right. The
   * `Content-Type` is the file's OWN `type` (what the browser knows about the file),
   * never an extension guess, because the server compares it against the shared
   * allowlist — `describeUploadProblem` is run by the caller BEFORE this is called, so
   * a type the API refuses should never reach the network.
   *
   * The bytes are sent as the raw body. Nothing else crosses: the file's NAME is not
   * sent, because the download route writes a fixed literal filename derived from the
   * stored type (a caller-controlled name in a header splits the response), so a name
   * here would be data nothing reads.
   */
  uploadDocument(
    workspaceId: string,
    domain: DocumentDomain,
    file: { readonly bytes: Uint8Array; readonly mime: string },
  ): Promise<DocumentUploadDto>;
  /**
   * GET /workspaces/:id/documents/:documentId — the file's BYTES.
   *
   * The same `requestBlob` the Schedule E CSV uses, deliberately: one endpoint
   * returned a file before this one, and a second blob path would be a second place
   * the `Accept` header and the error envelope are got right or wrong. The route
   * answers with `Content-Disposition: attachment` and writes an `audit_log` row per
   * read, so every open of a lease PDF or a receipt photo is recorded.
   *
   * A MISSING attachment and an id belonging to another workspace are both 404s with
   * DIFFERENT codes (`document_bytes_missing` vs `not_found`), and both arrive as an
   * `ApiError` — the caller renders `uploadErrorMessage` rather than a bare status.
   */
  downloadDocument(workspaceId: string, documentId: string): Promise<Blob>;
  /**
   * POST /workspaces/:id/payments — record money a landlord received.
   *
   * `amountCents` is the magnitude the landlord types; the API negates it into the
   * ledger's sign convention. The response reports where the charge landed and, for
   * an overpayment, how much of the payment was a credit.
   *
   * There is deliberately no update and no delete: the ledger is append-only, and a
   * mistake is corrected by appending a correction.
   *
   * The response is the whole movement (`PaymentRecordedDto`: the row, where the
   * charge landed, any overpayment credit, and whether this was a replay of an
   * earlier submission with the same `clientRequestId`), not the bare payment row.
   */
  recordPayment(workspaceId: string, body: PaymentCreateBody): Promise<PaymentRecordedDto>;
  /**
   * GET /workspaces/:id/expenses — every cost recorded against a building, newest
   * date first.
   *
   * This is `expense_entries` (property-scoped), NOT the ledger's `expense` row
   * (lease-attributed and append-only). It is an ordinary record, which is why
   * creating one and removing one are both offered here — the opposite of the
   * ledger, whose missing PATCH and DELETE are the invariant.
   *
   * There is deliberately **no `updateExpense`**: the PATCH route exists and is
   * proved by the API suite, and a client method with no caller would be the first
   * half of a capability nobody has built — the rule the ledger, rent-charge and
   * maintenance clients follow. (The route is what makes the record ordinary; the
   * screen does not offer an edit form yet.)
   */
  listExpenses(workspaceId: string): Promise<ExpenseListResponse>;
  /**
   * POST /workspaces/:id/expenses — record a cost against a building.
   *
   * `amountCents` is what the SPA converted from the field with `parseDollarInput`;
   * the API never accepts a float. `receiptFileId` is the id of a document already
   * uploaded with the `expense` domain (`uploadDocument`); the route verifies it is a
   * file of THIS workspace for THIS purpose, and refuses one that is not.
   */
  createExpense(
    workspaceId: string,
    body: ExpenseEntryCreateBody,
  ): Promise<ApiExpenseEntry>;
  /**
   * DELETE /workspaces/:id/expenses/:expenseId — remove a mistyped cost.
   *
   * A hard delete, unlike the ledger's absent one: this table is not append-only
   * and the row is not a posting.
   */
  deleteExpense(workspaceId: string, expenseId: string): Promise<void>;
  /**
   * GET /workspaces/:id/properties/:propertyId/pnl — one building's basic P&L.
   *
   * Income is real (`rent_charges` and counted payment rows attributed through
   * them); expenses are what was entered. The payload carries a `basis` sentence
   * saying what the total is not, and the screen renders it.
   */
  getPropertyPnl(workspaceId: string, propertyId: string): Promise<PropertyPnlResponse>;
  /** POST /workspaces/:id/properties */
  createProperty(workspaceId: string, body: PropertyCreateBody): Promise<ApiProperty>;
  /** GET /workspaces/:id/properties/:propertyId — the property and its units. */
  getProperty(workspaceId: string, propertyId: string): Promise<ApiPropertyWithUnits>;
  /** DELETE /workspaces/:id/properties/:propertyId — cascades to its units. */
  deleteProperty(workspaceId: string, propertyId: string): Promise<void>;
  /** POST /workspaces/:id/properties/:propertyId/units */
  createUnit(
    workspaceId: string,
    propertyId: string,
    body: UnitCreateBody,
  ): Promise<ApiUnit>;
  /**
   * GET /workspaces/:id/guarantors — the roster, with the tenant/lease/property
   * context each row has and the counts folded from those rows.
   *
   * Every row carries `consentState` and `mayContact`, computed server-side by the
   * shared pure functions. `mayContact` is a PERMISSION, not a capability:
   * notifications (T1.8) do not exist, so nothing sends and no control here may
   * imply otherwise.
   */
  listGuarantors(workspaceId: string): Promise<GuarantorListResponse>;
  /**
   * POST /workspaces/:id/guarantors — record a guarantor contact.
   *
   * `consentRecorded` defaults to `false` in the database and is refused as `true`
   * unless the same body names a `consentSource`, because a consent fact with no
   * recorded source is not a consent fact. This method is not a consent recorder:
   * `recordGuarantorConsent` is the one path that flips the flag.
   *
   * There is deliberately **no `updateGuarantor`**: the PATCH route exists and is
   * proved by the API suite, and the screen offers no edit form — a client method
   * with no caller would be the first half of a capability nobody has built, the
   * rule the ledger, rent-charge, maintenance and expense clients follow.
   */
  createGuarantor(
    workspaceId: string,
    body: GuarantorContactCreateBody,
  ): Promise<ApiGuarantorContact>;
  /**
   * POST /workspaces/:id/guarantors/:guarantorId/consent — the explicit,
   * deliberate act that records consent, or withdraws it.
   *
   * The ONLY path that flips `consent_recorded`. Editing a phone number cannot, and
   * the API refuses a PATCH that tries.
   */
  recordGuarantorConsent(
    workspaceId: string,
    guarantorId: string,
    body: GuarantorConsentBody,
  ): Promise<ApiGuarantorContact>;
  /**
   * DELETE /workspaces/:id/guarantors/:guarantorId — remove a contact recorded by
   * mistake.
   *
   * A hard delete, unlike a ledger row. A guarantor a lease tenant points at is
   * refused with `409 guarantor_in_use` and destroys nothing — that reference is
   * history.
   */
  deleteGuarantor(workspaceId: string, guarantorId: string): Promise<void>;
  /**
   * GET /workspaces/:id/turnover — every unit with its position between tenancies,
   * folded from real leases, the building's timezone and the open maintenance on
   * the unit.
   *
   * A read only, and there is deliberately nothing else here: this screen changes
   * nothing, so no transition, no status write and no "mark make-ready" appears
   * beside it. A make-ready task is an open maintenance request on the unit
   * (`createMaintenanceRequest`), which is why no separate method exists for one.
   */
  getTurnover(workspaceId: string): Promise<TurnoverResponse>;
  /**
   * GET /workspaces/:id/reports/schedule-e — what the CSV export would contain.
   *
   * A metadata read: whole-row counts, the column list, and the API's own sentences
   * about the period semantics and about what the file is NOT. It carries no money
   * figure, so the page can say something true about the export without printing a
   * total it did not compute.
   */
  getScheduleEReport(
    workspaceId: string,
    params?: ScheduleEReportParams,
  ): Promise<ScheduleEReportResponse>;
  /**
   * GET /workspaces/:id/reports/schedule-e.csv — the document itself, as a BLOB.
   *
   * **This response is not JSON**, so it does not go through `request<T>`: that
   * helper calls `response.json()` on any 2xx, which would throw on a CSV body.
   * It is the client's only non-JSON read, and it is kept small and explicit rather
   * than generalised — one endpoint returns a file, and nothing else does.
   *
   * The filename is deliberately NOT parsed back out of `Content-Disposition`: the
   * server writes a fixed literal that the kernel owns
   * (`SCHEDULE_E_CSV_FILENAME`), and the caller names the saved file with the same
   * constant. Reading a header here would add a parser to agree with a constant
   * that already cannot drift.
   *
   * The BOM and the CRLF line endings are in the bytes; a Blob carries them intact.
   */
  downloadScheduleEReportCsv(
    workspaceId: string,
    params?: ScheduleEReportParams,
  ): Promise<Blob>;
  /**
   * GET /workspaces/:id/{ledger,payments,tenants}/export.csv — a whole-set export.
   *
   * Unlike the Schedule E file, the name carries the workspace-local DATE the server
   * computed, so it is read back from `Content-Disposition` (strictly: only a plain
   * `neighboros-…csv` token is accepted, else a fixed fallback) rather than guessed
   * from the browser's clock and zone.
   */
  downloadExportCsv(
    workspaceId: string,
    kind: ExportKind,
  ): Promise<{ readonly blob: Blob; readonly filename: string }>;
  /**
   * GET /workspaces/:id/members — who is in this workspace, with their role.
   *
   * A read. Changing a role, removing a person and renaming the workspace are the
   * owner-only methods that follow it.
   */
  listMembers(workspaceId: string): Promise<TeamListResponse>;
  /** PATCH /workspaces/:id/members/:userId. Owner only; 409 `last_owner` when it would orphan the workspace. */
  updateMemberRole(
    workspaceId: string,
    userId: string,
    role: AssignableTeamRole,
  ): Promise<MemberRoleChangedDto>;
  /** DELETE /workspaces/:id/members/:userId. Owner only; 204. 409 `last_owner` for the last owner. */
  removeMember(workspaceId: string, userId: string): Promise<void>;
  /** PATCH /workspaces/:id — rename the workspace. Owner only. */
  renameWorkspace(workspaceId: string, name: string): Promise<WorkspaceRenamedDto>;
  /**
   * GET /workspaces/:id/settings — the ONE setting any code reads.
   *
   * The payload carries the current `proration` method (or `null`), the methods the
   * platform supports from the same kernel constant the rent-charge generator
   * validates against, and `notBuilt`: the §5 settings this page deliberately has no
   * control for because nothing reads them.
   */
  getWorkspaceSettings(workspaceId: string): Promise<WorkspaceSettingsResponse>;
  /** GET /workspaces/:id/invitations — pending invitations only. Owner only. */
  listInvitations(workspaceId: string): Promise<InvitationListResponse>;
  /**
   * POST /workspaces/:id/invitations — invite an address to join as a `member`. Owner only. 409 when
   * the person is already a member or already invited, 429 at the pending cap. `joinUrl` comes back
   * only when no email could be sent.
   */
  createInvitation(workspaceId: string, email: string): Promise<InvitationCreatedResponse>;
  /** DELETE /workspaces/:id/invitations/:invitationId — revoke; idempotent. Owner only. */
  revokeInvitation(workspaceId: string, invitationId: string): Promise<void>;
  /**
   * POST /workspaces/invitations/accept — join the workspace the emailed link names. Any signed-in
   * user; one generic 400 for every kind of failure.
   */
  acceptInvitation(workspaceId: string, token: string): Promise<InvitationAcceptedResponse>;
  /**
   * PATCH /workspaces/:id/settings — set the proration method, or unset it.
   *
   * **Owner only on the server**: a `member` gets a 403, because this changes how
   * tenants are billed. The body has exactly one key, and `proration: null` is the
   * explicit "not configured / no proration" state.
   */
  updateWorkspaceSettings(
    workspaceId: string,
    body: WorkspaceSettingsUpdateBody,
  ): Promise<WorkspaceSettingsResponse>;
}

export interface CreateApiClientOptions {
  /**
   * Where the API is, no trailing slash (guaranteed by `readWebEnv`): `/api` for the
   * same-origin default, or an absolute origin in a test.
   */
  readonly baseUrl: string;
  /** Injectable for tests; defaults to the global `fetch`. */
  readonly fetchImpl?: typeof fetch;
}

/** Request options the client actually uses — a narrow slice of `RequestInit`. */
interface RequestOptions {
  readonly method?: "GET" | "POST" | "PATCH" | "DELETE";
  readonly body?: string;
}

/** Convert a non-2xx response into an `ApiError`, preserving code and field issues. */
async function toApiError(response: Response): Promise<ApiError> {
  let code = "http_error";
  let message = `Request failed with status ${String(response.status)}.`;
  let issues: readonly ApiFieldIssue[] = [];
  try {
    const body = (await response.json()) as ErrorEnvelope;
    const envelope = body.error;
    if (typeof envelope?.code === "string" && envelope.code.length > 0) {
      code = envelope.code;
      message = typeof envelope.message === "string" ? envelope.message : message;
    }
    // The API has always returned per-field problems in `details`; the client used
    // to drop them, so a rejected save read "invalid property" with no clue which
    // field was wrong. Keep only well-formed entries — a malformed `details` must
    // not break rendering of the message itself.
    if (Array.isArray(envelope?.details)) {
      issues = envelope.details.flatMap((entry) => {
        if (entry === null || typeof entry !== "object") return [];
        const candidate = entry as { path?: unknown; message?: unknown };
        if (!Array.isArray(candidate.path)) return [];
        if (typeof candidate.message !== "string") return [];
        return [
          {
            path: candidate.path.filter(
              (segment): segment is string | number =>
                typeof segment === "string" || typeof segment === "number",
            ),
            message: candidate.message,
          },
        ];
      });
    }
  } catch {
    // Non-JSON body (a proxy error page, a platform 502). Keep the defaults:
    // the status code is the useful signal, and we must never surface HTML.
  }
  return new ApiError(response.status, code, message, issues);
}

export function createApiClient(options: CreateApiClientOptions): ApiClient {
  const doFetch = options.fetchImpl ?? globalThis.fetch;

  async function request<T>(path: string, requestOptions: RequestOptions = {}): Promise<T> {
    const headers = new Headers();
    headers.set("Accept", "application/json");

    // `same-origin` is the fetch default, stated so nobody "fixes" this to `include`: the
    // session cookie rides on a same-origin request and must never be sent cross-origin.
    const init: RequestInit = {
      method: requestOptions.method ?? "GET",
      headers,
      credentials: "same-origin",
    };
    if (requestOptions.body !== undefined) {
      headers.set("Content-Type", "application/json");
      init.body = requestOptions.body;
    }

    const response = await doFetch(`${options.baseUrl}${path}`, init);
    if (!response.ok) {
      throw await toApiError(response);
    }
    // 204 has no body: DELETE returns one, and `response.json()` on an empty
    // body throws a SyntaxError that would look like a transport failure.
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  /** Path-encode a segment that came from a route param or a server id. */
  function segment(value: string): string {
    return encodeURIComponent(value);
  }

  /**
   * The report's query string, or the empty string when no filter is set.
   *
   * A blank control (`""`) is dropped rather than sent: the API treats an absent
   * bound as "no bound", and sending `from=` would be the same request with one
   * more representation of "none" — the rule the expense and property forms follow
   * for a cleared field.
   */
  function reportQuery(params: ScheduleEReportParams | undefined): string {
    const search = new URLSearchParams();
    if (params?.from !== undefined && params.from.length > 0) search.set("from", params.from);
    if (params?.to !== undefined && params.to.length > 0) search.set("to", params.to);
    if (params?.propertyId !== undefined && params.propertyId.length > 0) {
      search.set("propertyId", params.propertyId);
    }
    const query = search.toString();
    return query.length === 0 ? "" : `?${query}`;
  }

  /**
   * A response read as BYTES, for the endpoints that are not JSON.
   *
   * Deliberately separate from `request<T>` rather than a flag on it: a flag would
   * make every caller decide, and the wrong choice fails at runtime on a body that
   * is not JSON. A blob also preserves the BOM and the CRLF bytes the CSV is
   * written with, and the exact bytes of a stored document.
   */
  async function requestBlob(path: string): Promise<Blob> {
    const headers = new Headers();
    headers.set("Accept", "text/csv");

    const response = await doFetch(`${options.baseUrl}${path}`, {
      method: "GET",
      headers,
      credentials: "same-origin",
    });
    if (!response.ok) {
      // The failure path is still the Worker's JSON envelope, so the same
      // `toApiError` reports it with its code and its per-field issues.
      throw await toApiError(response);
    }
    return response.blob();
  }

  async function requestExport(
    path: string,
    fallbackName: string,
  ): Promise<{ readonly blob: Blob; readonly filename: string }> {
    const headers = new Headers();
    headers.set("Accept", "text/csv");
    const response = await doFetch(`${options.baseUrl}${path}`, {
      method: "GET",
      headers,
      credentials: "same-origin",
    });
    if (!response.ok) throw await toApiError(response);
    const match = /filename="(neighboros-[a-z]+-\d{4}-\d{2}-\d{2}\.csv)"/.exec(
      response.headers.get("Content-Disposition") ?? "",
    );
    return { blob: await response.blob(), filename: match?.[1] ?? fallbackName };
  }

  /**
   * The upload: a raw body with the file's OWN media type, and the purpose as a query
   * parameter.
   *
   * The mirror image of `requestBlob`, and it exists for the same reason on the write
   * side: `request<T>` would `JSON.stringify` a file and claim it is JSON. `Accept` is
   * `application/json` because the RESPONSE is the created document's metadata, not
   * the file.
   *
   * The `Content-Type` header is passed to `Headers.set`, which validates it and
   * throws on a malformed value — which is the right behaviour: a media type a browser
   * cannot spell is one the server would refuse anyway. The file's own `type` is what
   * goes, never a guess from the name: the server compares it against the shared
   * allowlist, and a guess would make the client and the route disagree about the same
   * bytes.
   */
  async function uploadFile(
    path: string,
    file: { readonly bytes: Uint8Array; readonly mime: string },
  ): Promise<DocumentUploadDto> {
    const headers = new Headers();
    headers.set("Accept", "application/json");
    headers.set("Content-Type", file.mime);

    const response = await doFetch(`${options.baseUrl}${path}`, {
      method: "POST",
      headers,
      credentials: "same-origin",
      // The array itself is the body. The cast is forced by the DOM's own types rather
      // than by anything about this value: `BodyInit` admits `ArrayBufferView`, but a
      // `Uint8Array` is generic over its backing buffer and TS 5.6 will not accept a
      // `Uint8Array<ArrayBufferLike>` there. The alternative — sending `.buffer` — would
      // ship whatever else shares the allocation, which for bytes read from a `File` is
      // exactly the file and for any other array is a bug waiting to be a data leak.
      body: file.bytes as BodyInit,
    });
    if (!response.ok) {
      throw await toApiError(response);
    }
    return (await response.json()) as DocumentUploadDto;
  }

  return {
    me: () => request<MeResponse>("/me"),
    createWorkspace: (name: string) =>
      request<ApiWorkspace>("/workspaces", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    overview: (workspaceId: string) =>
      request<OverviewResponse>(`/workspaces/${segment(workspaceId)}/overview`),
    listProperties: (workspaceId: string) =>
      request<PropertyListResponse>(`/workspaces/${segment(workspaceId)}/properties`),
    listUnits: (workspaceId: string) =>
      request<UnitListResponse>(`/workspaces/${segment(workspaceId)}/units`),
    getUnit: (workspaceId: string, unitId: string) =>
      request<ApiUnitDetail>(
        `/workspaces/${segment(workspaceId)}/units/${segment(unitId)}`,
      ),
    listLeases: (workspaceId: string) =>
      request<LeaseListResponse>(`/workspaces/${segment(workspaceId)}/leases`),
    getLease: (workspaceId: string, leaseId: string) =>
      request<LeaseDetailResponse>(
        `/workspaces/${segment(workspaceId)}/leases/${segment(leaseId)}`,
      ),
    getLeaseLedger: (workspaceId: string, leaseId: string) =>
      request<LeaseLedgerResponse>(
        `/workspaces/${segment(workspaceId)}/leases/${segment(leaseId)}/ledger`,
      ),
    getLeaseRentCharges: (workspaceId: string, leaseId: string) =>
      request<LeaseRentChargesResponse>(
        `/workspaces/${segment(workspaceId)}/leases/${segment(leaseId)}/rent-charges`,
      ),
    createLease: (workspaceId: string, body: LeaseCreateBody) =>
      request<ApiLease>(`/workspaces/${segment(workspaceId)}/leases`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    transitionLease: (workspaceId: string, leaseId: string, to: LeaseStatus) =>
      request<ApiLease>(
        `/workspaces/${segment(workspaceId)}/leases/${segment(leaseId)}/transitions`,
        { method: "POST", body: JSON.stringify({ to }) },
      ),
    updateLease: (workspaceId: string, leaseId: string, body: LeaseUpdateBody) =>
      request<LeaseDetailResponse>(
        `/workspaces/${segment(workspaceId)}/leases/${segment(leaseId)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    listTenants: (workspaceId: string) =>
      request<TenantListResponse>(`/workspaces/${segment(workspaceId)}/tenants`),
    getTenant: (workspaceId: string, tenantId: string) =>
      request<ApiTenantDetail>(
        `/workspaces/${segment(workspaceId)}/tenants/${segment(tenantId)}`,
      ),
    updateTenant: (
      workspaceId: string,
      tenantId: string,
      body: LeaseTenantUpdateBody,
    ) =>
      request<ApiTenantDetail>(
        `/workspaces/${segment(workspaceId)}/tenants/${segment(tenantId)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
    listLedger: (workspaceId: string, page?: PageParams) =>
      request<LedgerListResponse>(`/workspaces/${segment(workspaceId)}/ledger${pageQuery(page)}`),
    correctLedgerEntry: (workspaceId: string, entryId: string, body: LedgerCorrectionCreateBody) =>
      request<ApiLedgerEntry>(
        `/workspaces/${segment(workspaceId)}/ledger/${segment(entryId)}/correction`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    listRentCharges: (workspaceId: string, page?: PageParams) =>
      request<RentChargeListResponse>(
        `/workspaces/${segment(workspaceId)}/rent-charges${pageQuery(page)}`,
      ),
    generateRentCharges: (workspaceId: string) =>
      request<RentChargeGenerationResponse>(
        `/workspaces/${segment(workspaceId)}/rent-charges/generate`,
        { method: "POST" },
      ),
    listPayments: (workspaceId: string, page?: PageParams) =>
      request<PaymentListResponse>(
        `/workspaces/${segment(workspaceId)}/payments${pageQuery(page)}`,
      ),
    listActivity: (workspaceId: string, page?: PageParams) =>
      request<ActivityPageDto>(`/workspaces/${segment(workspaceId)}/activity${pageQuery(page)}`),
    listBalances: (workspaceId: string) =>
      request<BalancesResponse>(`/workspaces/${segment(workspaceId)}/balances`),
    listMaintenance: (workspaceId: string) =>
      request<MaintenanceListResponse>(`/workspaces/${segment(workspaceId)}/maintenance`),
    createMaintenanceRequest: (workspaceId: string, body: MaintenanceRequestCreateBody) =>
      request<ApiMaintenanceRequest>(`/workspaces/${segment(workspaceId)}/maintenance`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    getMaintenanceRequest: (workspaceId: string, requestId: string) =>
      request<MaintenanceRequestDetailResponse>(
        `/workspaces/${segment(workspaceId)}/maintenance/${segment(requestId)}`,
      ),
    transitionMaintenanceRequest: (
      workspaceId: string,
      requestId: string,
      to: MaintenanceStatus,
    ) =>
      request<ApiMaintenanceRequest>(
        `/workspaces/${segment(workspaceId)}/maintenance/${segment(requestId)}/transitions`,
        { method: "POST", body: JSON.stringify({ to }) },
      ),
    addMaintenanceNote: (
      workspaceId: string,
      requestId: string,
      body: MaintenanceEventCreateBody,
    ) =>
      request<ApiMaintenanceEvent>(
        `/workspaces/${segment(workspaceId)}/maintenance/${segment(requestId)}/events`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    uploadDocument: (
      workspaceId: string,
      domain: DocumentDomain,
      file: { readonly bytes: Uint8Array; readonly mime: string },
    ) =>
      uploadFile(
        `/workspaces/${segment(workspaceId)}/documents?domain=${segment(domain)}`,
        file,
      ),
    downloadDocument: (workspaceId: string, documentId: string) =>
      requestBlob(
        `/workspaces/${segment(workspaceId)}/documents/${segment(documentId)}`,
      ),
    recordPayment: (workspaceId: string, body: PaymentCreateBody) =>
      request<PaymentRecordedDto>(`/workspaces/${segment(workspaceId)}/payments`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    listExpenses: (workspaceId: string) =>
      request<ExpenseListResponse>(`/workspaces/${segment(workspaceId)}/expenses`),
    createExpense: (workspaceId: string, body: ExpenseEntryCreateBody) =>
      request<ApiExpenseEntry>(`/workspaces/${segment(workspaceId)}/expenses`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    deleteExpense: (workspaceId: string, expenseId: string) =>
      request<void>(`/workspaces/${segment(workspaceId)}/expenses/${segment(expenseId)}`, {
        method: "DELETE",
      }),
    getPropertyPnl: (workspaceId: string, propertyId: string) =>
      request<PropertyPnlResponse>(
        `/workspaces/${segment(workspaceId)}/properties/${segment(propertyId)}/pnl`,
      ),
    createProperty: (workspaceId: string, body: PropertyCreateBody) =>
      request<ApiProperty>(`/workspaces/${segment(workspaceId)}/properties`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    getProperty: (workspaceId: string, propertyId: string) =>
      request<ApiPropertyWithUnits>(
        `/workspaces/${segment(workspaceId)}/properties/${segment(propertyId)}`,
      ),
    deleteProperty: (workspaceId: string, propertyId: string) =>
      request<void>(`/workspaces/${segment(workspaceId)}/properties/${segment(propertyId)}`, {
        method: "DELETE",
      }),
    createUnit: (workspaceId: string, propertyId: string, body: UnitCreateBody) =>
      request<ApiUnit>(
        `/workspaces/${segment(workspaceId)}/properties/${segment(propertyId)}/units`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    listGuarantors: (workspaceId: string) =>
      request<GuarantorListResponse>(`/workspaces/${segment(workspaceId)}/guarantors`),
    createGuarantor: (workspaceId: string, body: GuarantorContactCreateBody) =>
      request<ApiGuarantorContact>(`/workspaces/${segment(workspaceId)}/guarantors`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    recordGuarantorConsent: (
      workspaceId: string,
      guarantorId: string,
      body: GuarantorConsentBody,
    ) =>
      request<ApiGuarantorContact>(
        `/workspaces/${segment(workspaceId)}/guarantors/${segment(guarantorId)}/consent`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    deleteGuarantor: (workspaceId: string, guarantorId: string) =>
      request<void>(
        `/workspaces/${segment(workspaceId)}/guarantors/${segment(guarantorId)}`,
        { method: "DELETE" },
      ),
    getTurnover: (workspaceId: string) =>
      request<TurnoverResponse>(`/workspaces/${segment(workspaceId)}/turnover`),
    getScheduleEReport: (workspaceId: string, params?: ScheduleEReportParams) =>
      request<ScheduleEReportResponse>(
        `/workspaces/${segment(workspaceId)}/reports/schedule-e${reportQuery(params)}`,
      ),
    downloadScheduleEReportCsv: (workspaceId: string, params?: ScheduleEReportParams) =>
      requestBlob(
        `/workspaces/${segment(workspaceId)}/reports/schedule-e.csv${reportQuery(params)}`,
      ),
    downloadExportCsv: (workspaceId: string, kind: ExportKind) =>
      requestExport(
        `/workspaces/${segment(workspaceId)}/${kind}/export.csv`,
        `neighboros-${kind}.csv`,
      ),
    listMembers: (workspaceId: string) =>
      request<TeamListResponse>(`/workspaces/${segment(workspaceId)}/members`),
    updateMemberRole: (workspaceId: string, userId: string, role: AssignableTeamRole) =>
      request<MemberRoleChangedDto>(
        `/workspaces/${segment(workspaceId)}/members/${segment(userId)}`,
        { method: "PATCH", body: JSON.stringify({ role }) },
      ),
    removeMember: (workspaceId: string, userId: string) =>
      request<void>(`/workspaces/${segment(workspaceId)}/members/${segment(userId)}`, {
        method: "DELETE",
      }),
    renameWorkspace: (workspaceId: string, name: string) =>
      request<WorkspaceRenamedDto>(`/workspaces/${segment(workspaceId)}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    listInvitations: (workspaceId: string) =>
      request<InvitationListResponse>(`/workspaces/${segment(workspaceId)}/invitations`),
    createInvitation: (workspaceId: string, email: string) =>
      request<InvitationCreatedResponse>(`/workspaces/${segment(workspaceId)}/invitations`, {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    revokeInvitation: (workspaceId: string, invitationId: string) =>
      request<void>(
        `/workspaces/${segment(workspaceId)}/invitations/${segment(invitationId)}`,
        { method: "DELETE" },
      ),
    acceptInvitation: (workspaceId: string, token: string) =>
      request<InvitationAcceptedResponse>("/workspaces/invitations/accept", {
        method: "POST",
        body: JSON.stringify({ workspaceId, token }),
      }),
    getWorkspaceSettings: (workspaceId: string) =>
      request<WorkspaceSettingsResponse>(`/workspaces/${segment(workspaceId)}/settings`),
    updateWorkspaceSettings: (
      workspaceId: string,
      body: WorkspaceSettingsUpdateBody,
    ) =>
      request<WorkspaceSettingsResponse>(`/workspaces/${segment(workspaceId)}/settings`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  };
}
